// React Query hooks for the Wave D invoices module.
//
// All endpoints come from `endpoints.invoices`. The hooks encapsulate the
// query keys and cache invalidation rules: any create/edit/delete/send/PDF
// action invalidates `queryKeys.invoices.all` so list and detail panes
// refresh together.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useToast } from '../../../hooks/useToast';
import type {
  CreateInvoiceInput,
  InvoiceCompareResponse,
  InvoiceDetailResponse,
  InvoiceListResponse,
  InvoiceStatus,
  InvoiceWorkSourcesResponse,
  UpdateInvoiceInput,
} from '../../../types/api';

export interface InvoiceListFilters {
  page: number;
  limit?: number;
  status?: InvoiceStatus;
  customer_uuid?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
  source_id?: number;
}

export function useInvoiceList(filters: InvoiceListFilters) {
  return useQuery<InvoiceListResponse>({
    queryKey: queryKeys.invoices.list(filters as unknown as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.status) params.set('status', filters.status);
      if (filters.customer_uuid) params.set('customer_uuid', filters.customer_uuid);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.q) params.set('q', filters.q);
      if (filters.source_id !== undefined) {
        params.set('source_id', String(filters.source_id));
      }
      const res = await apiClient.get<InvoiceListResponse>(
        `${endpoints.invoices.list}?${params.toString()}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export interface InvoiceCompareInput {
  file: File;
  source_id: number;
  date_from: string; // YYYY-MM-DD
  date_to: string; // YYYY-MM-DD
}

/** SCC invoice compare — uploads the supplier PDF as multipart/form-data
 *  and returns the legacy comparison report. Mutation, not a query, so the
 *  caller controls when to invalidate downstream lists. */
export function useInvoiceCompare() {
  const toast = useToast();
  return useMutation({
    mutationFn: async (input: InvoiceCompareInput) => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('source_id', String(input.source_id));
      fd.append('date_from', input.date_from);
      fd.append('date_to', input.date_to);
      const res = await apiClient.post<InvoiceCompareResponse>(
        endpoints.invoices.compare,
        fd,
        // Let the browser set the multipart boundary header.
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data;
    },
    onError: () => {
      toast.error('Failed to compare the invoice. Check the PDF and try again.');
    },
  });
}

/** Dropdown source for the legacy "Work Source" filter on /invoices. */
export function useInvoiceWorkSources() {
  return useQuery<InvoiceWorkSourcesResponse>({
    queryKey: queryKeys.invoices.workSources,
    queryFn: async () => {
      const res = await apiClient.get<InvoiceWorkSourcesResponse>(
        endpoints.invoices.workSources,
      );
      return res.data;
    },
    // Reference data — invalidated by reference-data CRUD elsewhere; the
    // dropdown rarely changes during a session so cache aggressively.
    staleTime: 5 * 60_000,
  });
}

export function useInvoiceDetail(encId: string | undefined) {
  return useQuery<InvoiceDetailResponse>({
    queryKey: encId ? queryKeys.invoices.detail(encId) : ['invoices', 'detail', 'none'],
    queryFn: async () => {
      const res = await apiClient.get<InvoiceDetailResponse>(
        endpoints.invoices.detail(encId as string),
      );
      return res.data;
    },
    enabled: Boolean(encId),
    staleTime: 30_000,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateInvoiceInput) =>
      apiClient.post<InvoiceDetailResponse>(endpoints.invoices.create, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      toast.success('Invoice created.');
    },
    onError: () => {
      toast.error('Failed to create invoice. Please try again.');
    },
  });
}

export function useUpdateInvoice(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpdateInvoiceInput) =>
      apiClient.put<InvoiceDetailResponse>(endpoints.invoices.update(encId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      toast.success('Invoice updated.');
    },
    onError: () => {
      toast.error('Failed to update invoice. Please try again.');
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (encId: string) => apiClient.delete(endpoints.invoices.delete(encId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      toast.success('Invoice deleted.');
    },
    onError: () => {
      toast.error('Failed to delete invoice.');
    },
  });
}

export interface SendInvoiceInput {
  encId: string;
  to_email?: string;
}

export function useSendInvoice() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ encId, to_email }: SendInvoiceInput) =>
      apiClient.post<InvoiceDetailResponse>(
        endpoints.invoices.send(encId),
        to_email ? { to_email } : undefined,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(vars.encId) });
      toast.success('Invoice sent.');
    },
    onError: () => {
      toast.error('Failed to send invoice.');
    },
  });
}
